import { ApiErrorCode } from '~/core/errors/codes';
import {
  readApiErrorPayload,
  readMissingSessionErrorResponse,
  readTerminalSessionErrorResponse,
  redirectToSignInForMissingSession,
  redirectToSignInForSessionRecovery,
} from '~/core/session/client-session-recovery';
import {
  CLIENT_AUTH_RECOVERY_REQUIRED_HEADER,
  RECOVERY_HEADER_VALUE,
  SESSION_STATUS_ENDPOINT,
} from '~/core/session/session-recovery-constants';

const TERMINAL_FIREBASE_CLIENT_AUTH_CODES = new Set([
  'auth/id-token-revoked',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/user-token-expired',
]);

const CSRF_ERROR_CODES = new Set<ApiErrorCode>([
  ApiErrorCode.CSRF_TOKEN_MISSING,
  ApiErrorCode.CSRF_TOKEN_INVALID,
  ApiErrorCode.CSRF_SECRET_NOT_FOUND,
  ApiErrorCode.CSRF_CHECK_FAILED,
]);

export class SessionRecoveryStartedError extends Error {
  constructor(source: string) {
    super(`Session recovery started from ${source}`);
    this.name = 'SessionRecoveryStartedError';
  }
}

export function isTerminalFirebaseClientAuthError(error: unknown): boolean {
  return TERMINAL_FIREBASE_CLIENT_AUTH_CODES.has(getErrorCode(error) ?? '');
}

export async function recoverIfCurrentServerSessionIsTerminal(
  source: string,
  returnUrl?: string,
): Promise<boolean> {
  try {
    const response = await fetch(SESSION_STATUS_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'x-directordeck-recovery-source': source,
      },
    });

    if (await readTerminalSessionErrorResponse(response)) {
      redirectToSignInForSessionRecovery(returnUrl);
      return true;
    }
  } catch {
    return false;
  }

  return false;
}

export async function recoverIfFirebaseClientAuthError(
  error: unknown,
  source: string,
  returnUrl?: string,
): Promise<boolean> {
  if (!isTerminalFirebaseClientAuthError(error)) {
    return false;
  }

  return recoverIfCurrentServerSessionIsTerminal(source, returnUrl);
}

export async function recoverIfTerminalClientAuthSignal(
  source: string,
  returnUrl?: string,
): Promise<boolean> {
  return recoverIfCurrentServerSessionIsTerminal(source, returnUrl);
}

export async function recoverIfProtectedApiResponse(
  response: Response,
  source: string,
  options: { recoverMissingSession?: boolean; returnUrl?: string } = {},
): Promise<boolean> {
  if (hasTerminalClientAuthRecoverySignal(response)) {
    return recoverIfTerminalClientAuthSignal(source, options.returnUrl);
  }

  if (await readTerminalSessionErrorResponse(response)) {
    redirectToSignInForSessionRecovery(options.returnUrl);
    return true;
  }

  if (
    options.recoverMissingSession !== false &&
    (await readMissingSessionErrorResponse(response))
  ) {
    redirectToSignInForMissingSession(options.returnUrl);
    return true;
  }

  return false;
}

export async function throwIfProtectedApiResponseStartsRecovery(
  response: Response,
  source: string,
  options?: { recoverMissingSession?: boolean; returnUrl?: string },
): Promise<void> {
  if (await recoverIfProtectedApiResponse(response, source, options)) {
    throw new SessionRecoveryStartedError(source);
  }
}

export function hasTerminalClientAuthRecoverySignal(
  response: Response,
): boolean {
  return (
    response.headers?.get?.(CLIENT_AUTH_RECOVERY_REQUIRED_HEADER) ===
    RECOVERY_HEADER_VALUE
  );
}

export async function isAuthTokenInvalidResponse(
  response: Response,
): Promise<boolean> {
  if (response.status !== 401 && response.status !== 403) {
    return false;
  }

  const payload = await readApiErrorPayload(response);

  return (
    Boolean(payload) &&
    typeof payload === 'object' &&
    (payload as { code?: unknown }).code === ApiErrorCode.AUTH_TOKEN_INVALID
  );
}

export async function isCsrfFailureResponse(
  response: Response,
): Promise<boolean> {
  if (response.status !== 401) {
    return false;
  }

  const payload = await readApiErrorPayload(response);

  return (
    Boolean(payload) &&
    typeof payload === 'object' &&
    CSRF_ERROR_CODES.has(
      (payload as { code?: ApiErrorCode }).code as ApiErrorCode,
    )
  );
}

function getErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}
